import React, { useMemo, useState } from "react";
import liff from "@line/liff";
import { axiosInstance, extractApiErrorMessage } from "../../utils/axios.js";
import "./survey.scss";

// 友だち追加直後アンケート（小児科CRM）
// LIFF認証済みの患者として POST /api/medichat/line_surveys に回答を送る。
// 回答値は backend の enum キー（文字列）で送信する。

const Q1_OPTIONS = [
  { value: "hot", label: "今まさに相談したい症状・悩みがある" },
  { value: "warm", label: "具体的な悩みはないが、いざという時のために登録した" },
  { value: "cold", label: "まだ情報収集中・様子見" },
];

const Q2_OPTIONS = [
  { value: "night", label: "夜間・休日に小児科医へ相談したい" },
  { value: "triage", label: "病院に行くべきか迷った時に判断したい" },
  { value: "symptom", label: "発熱・咳・発疹など症状を相談したい" },
  { value: "rx", label: "薬を処方してほしい（オンライン診療）" },
  { value: "parenting", label: "育児・発達・授乳などを相談したい" },
  { value: "convenience", label: "通院の待ち時間・負担を減らしたい" },
];

const Q3_OPTIONS = [
  { value: "preg", label: "妊娠中" },
  { value: "0_6m", label: "0〜6ヶ月" },
  { value: "7_12m", label: "7〜12ヶ月" },
  { value: "1_2y", label: "1〜2歳" },
  { value: "3_5y", label: "3〜5歳" },
  { value: "6y", label: "6歳以上" },
];

const Q4_OPTIONS = [
  { value: "price", label: "費用がどのくらいか" },
  { value: "online_doubt", label: "オンラインで本当に診てもらえるのか（触診なしで大丈夫か）" },
  { value: "howto", label: "使い方・予約の仕方がわからない" },
  { value: "doctor_trust", label: "医師がどんな人か・信頼できるか" },
  { value: "privacy", label: "個人情報・プライバシー" },
  { value: "none", label: "特に不安はない" },
];

const Q5_OPTIONS = [
  { value: "price", label: "費用の安さ" },
  { value: "quality", label: "医師の質・専門性" },
  { value: "hours", label: "相談できる時間帯" },
  { value: "booking", label: "予約の取りやすさ" },
  { value: "usability", label: "使いやすさ" },
  { value: "privacy", label: "プライバシー" },
];

const Q6_OPTIONS = [
  { value: "sns_ad", label: "SNS広告" },
  { value: "sns_organic", label: "SNS投稿・口コミ" },
  { value: "search", label: "検索" },
  { value: "media", label: "メディア記事" },
  { value: "referral", label: "家族・友人" },
  { value: "clinic", label: "医療機関" },
  { value: "other", label: "その他" },
];

export const LineSurveyContainer = () => {
  const [q1Temp, setQ1Temp] = useState("");
  const [q2Needs, setQ2Needs] = useState([]);
  const [q3Ages, setQ3Ages] = useState([]);
  const [q4Barrier, setQ4Barrier] = useState("");
  const [q5Value, setQ5Value] = useState("");
  const [q6Source, setQ6Source] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [errorText, setErrorText] = useState("");

  const toggleMulti = (list, setList, value) => {
    setList(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);
  };

  // 必須: Q1・Q4（単一） / Q2・Q3（1つ以上）
  const canSubmit = useMemo(
    () => Boolean(q1Temp) && q2Needs.length > 0 && q3Ages.length > 0 && Boolean(q4Barrier),
    [q1Temp, q2Needs, q3Ages, q4Barrier]
  );

  const handleSubmit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setErrorText("");
    try {
      await axiosInstance.post("/api/medichat/line_surveys", {
        survey: {
          q1_temp: q1Temp,
          q2_needs: q2Needs,
          q3_ages: q3Ages,
          q4_barrier: q4Barrier,
          q5_value: q5Value || null,
          q6_source: q6Source || null,
        },
      });
      setDone(true);
    } catch (error) {
      setErrorText(extractApiErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  const closeLiff = () => {
    try {
      if (liff && liff.isInClient && liff.isInClient()) {
        liff.closeWindow();
      }
    } catch (_e) {
      // LIFF外で開かれている場合は何もしない
    }
  };

  if (done) {
    return (
      <div className="line-survey line-survey--done">
        <div className="line-survey__card">
          <h1 className="line-survey__thanks-title">ありがとうございました🐣</h1>
          <p className="line-survey__thanks-body">
            いただいた内容をもとに、お子さんに合わせたご案内をLINEでお送りします。
          </p>
          <button type="button" className="line-survey__submit" onClick={closeLiff}>
            とじる
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="line-survey">
      <header className="line-survey__header">
        <h1 className="line-survey__title">キュアナウ ご利用前アンケート</h1>
        <p className="line-survey__lead">
          30秒で、あなたに合ったご案内をさせてください。小児科の相談は保険診療、お子さまは医療費助成で自己負担0円です。
        </p>
      </header>

      <SingleQuestion
        label="今、お子さんのことで気になっていることはありますか？"
        required
        options={Q1_OPTIONS}
        value={q1Temp}
        onChange={setQ1Temp}
      />

      <MultiQuestion
        label="キュアナウをどんな時に使いたいですか？"
        note="当てはまるものをすべて選択してください"
        required
        options={Q2_OPTIONS}
        values={q2Needs}
        onToggle={(v) => toggleMulti(q2Needs, setQ2Needs, v)}
      />

      <MultiQuestion
        label="お子さんの年齢を教えてください"
        note="当てはまるものをすべて選択してください"
        required
        options={Q3_OPTIONS}
        values={q3Ages}
        onToggle={(v) => toggleMulti(q3Ages, setQ3Ages, v)}
      />

      <SingleQuestion
        label="オンライン小児科相談で、一番気になる点は？"
        required
        options={Q4_OPTIONS}
        value={q4Barrier}
        onChange={setQ4Barrier}
      />

      <SingleQuestion
        label="サービスを選ぶ時に最も重視するのは？"
        options={Q5_OPTIONS}
        value={q5Value}
        onChange={setQ5Value}
      />

      <SingleQuestion
        label="キュアナウをどこで知りましたか？"
        options={Q6_OPTIONS}
        value={q6Source}
        onChange={setQ6Source}
      />

      {errorText ? <p className="line-survey__error">{errorText}</p> : null}

      <button
        type="button"
        className="line-survey__submit"
        disabled={!canSubmit || submitting}
        onClick={handleSubmit}
      >
        {submitting ? "送信中…" : "送信する"}
      </button>
    </div>
  );
};

const SingleQuestion = ({ label, note, required, options, value, onChange }) => (
  <section className="line-survey__q">
    <h2 className="line-survey__q-title">
      {label}
      {required ? <span className="line-survey__req">必須</span> : null}
    </h2>
    {note ? <p className="line-survey__q-note">{note}</p> : null}
    <div className="line-survey__options">
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          className={`line-survey__option${value === opt.value ? " is-selected" : ""}`}
          aria-pressed={value === opt.value}
          onClick={() => onChange(value === opt.value ? "" : opt.value)}
        >
          {opt.label}
        </button>
      ))}
    </div>
  </section>
);

const MultiQuestion = ({ label, note, required, options, values, onToggle }) => (
  <section className="line-survey__q">
    <h2 className="line-survey__q-title">
      {label}
      {required ? <span className="line-survey__req">必須</span> : null}
    </h2>
    {note ? <p className="line-survey__q-note">{note}</p> : null}
    <div className="line-survey__options">
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          className={`line-survey__option${values.includes(opt.value) ? " is-selected" : ""}`}
          aria-pressed={values.includes(opt.value)}
          onClick={() => onToggle(opt.value)}
        >
          {opt.label}
        </button>
      ))}
    </div>
  </section>
);

export default LineSurveyContainer;
